<template>
  <a-modal
    :open="visible"
    :title="modalTitle"
    width="80%"
    :footer="null"
    class="test-case-detail-modal"
    @cancel="handleClose"
  >
    <div v-if="testCase" class="test-case-detail-content">
      <!-- 测试用例基本信息 -->
      <div class="mb-6 border rounded-lg p-4" :class="getPriorityBorderClass()">
        <div class="mb-4 flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <h4 class="mb-2 text-lg font-semibold text-gray-800">{{ testCase.test_case_name }}</h4>
            <p class="mb-3 text-sm text-gray-600">{{ testCase.test_case_summary || '暂无描述' }}</p>
          </div>
          <div class="flex flex-shrink-0 items-center space-x-2">
            <span class="rounded px-3 py-1 text-sm font-medium" :class="getPriorityClass()">
              {{ getPriorityText() }}
            </span>
            <a-button v-if="!adopted" type="primary" :loading="adopting" @click="handleAdopt">
              <template #icon><plus-outlined /></template>
              采纳到计划
            </a-button>
            <a-tag v-else color="success">
              <template #icon><check-outlined /></template>
              已采纳
            </a-tag>
          </div>
        </div>

        <!-- 关键指标 -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="rounded-lg bg-blue-50 p-3 text-center">
            <div class="text-2xl font-bold text-blue-600">{{ getCorrelationScore() }}</div>
            <div class="text-sm text-gray-600">关联度评分</div>
          </div>
          <div v-if="testCase.estimated_execution_time" class="rounded-lg bg-green-50 p-3 text-center">
            <div class="text-2xl font-bold text-green-600">{{ testCase.estimated_execution_time }}min</div>
            <div class="text-sm text-gray-600">预计执行时间</div>
          </div>
          <div v-if="testCase.risk_score" class="rounded-lg p-3 text-center" :class="getRiskBackgroundClass()">
            <div class="text-2xl font-bold" :class="getRiskTextClass()">{{ getRiskScore() }}</div>
            <div class="text-sm text-gray-600">风险评分</div>
          </div>
        </div>
      </div>

      <!-- 标签页内容 -->
      <a-tabs v-model:activeKey="activeTab" type="card">
        <a-tab-pane key="ai-analysis" tab="AI分析">
          <div class="ai-analysis-content space-y-6">
            <!-- AI推荐原因 -->
            <div class="section">
              <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                <icon-vue icon="mdi:lightbulb-outline" class="mr-2 text-yellow-600" />
                AI推荐原因
              </h5>
              <div class="border-l-4 border-yellow-400 rounded-lg bg-yellow-50 p-4">
                <p class="text-sm text-gray-700">
                  {{ testCase.recommendation_reason || getDefaultRecommendationReason() }}
                </p>
              </div>
            </div>

            <!-- 关联的代码文件 -->
            <div v-if="testCase.related_files && testCase.related_files.length > 0" class="section">
              <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                <icon-vue icon="mdi:file-code-outline" class="mr-2 text-purple-600" />
                关联代码文件
              </h5>
              <div class="space-y-2">
                <div
                  v-for="(file, index) in testCase.related_files"
                  :key="index"
                  class="flex items-center justify-between border border-purple-200 rounded-lg bg-purple-50 p-3"
                >
                  <div class="flex items-center space-x-2">
                    <icon-vue icon="mdi:file-code" class="text-purple-600" />
                    <span class="text-sm font-mono text-gray-700">{{ getFileDisplayName(file) }}</span>
                  </div>
                  <span v-if="getFileCorrelation(file)" class="rounded bg-purple-100 px-2 py-1 text-xs text-purple-600">
                    关联度: {{ getFileCorrelation(file) }}%
                  </span>
                </div>
              </div>
            </div>

            <!-- 测试标签 -->
            <div v-if="testCase.tags && testCase.tags.length > 0" class="section">
              <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                <icon-vue icon="mdi:tag-multiple-outline" class="mr-2 text-orange-600" />
                测试标签
              </h5>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(tag, index) in testCase.tags"
                  :key="index"
                  class="border border-orange-200 rounded-full bg-orange-100 px-3 py-1 text-sm text-orange-700"
                >
                  {{ tag }}
                </span>
              </div>
            </div>

            <!-- 覆盖的函数 -->
            <div v-if="testCase.functions && testCase.functions.length > 0" class="section">
              <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                <icon-vue icon="mdi:function" class="mr-2 text-indigo-600" />
                覆盖的函数
              </h5>
              <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div
                  v-for="(func, index) in testCase.functions"
                  :key="index"
                  class="border border-indigo-200 rounded bg-indigo-50 p-2"
                >
                  <span class="text-sm font-mono text-indigo-700">{{ func }}</span>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>

        <a-tab-pane key="case-detail" tab="用例详情">
          <div class="case-detail-content">
            <div v-if="caseDetailLoading" class="py-8 text-center">
              <a-spin size="large" />
              <p class="mt-4 text-gray-500">正在加载用例详情...</p>
            </div>

            <div v-else-if="caseDetail" class="space-y-6">
              <!-- 测试步骤 -->
              <div v-if="caseDetail.test_steps && caseDetail.test_steps.length > 0" class="section">
                <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                  <icon-vue icon="mdi:format-list-numbered" class="mr-2 text-blue-600" />
                  测试步骤
                </h5>
                <div class="space-y-2">
                  <div
                    v-for="(step, index) in caseDetail.test_steps"
                    :key="index"
                    class="flex items-start rounded-lg bg-gray-50 p-3 space-x-3"
                  >
                    <span
                      class="h-6 w-6 flex flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs text-white"
                    >
                      {{ index + 1 }}
                    </span>
                    <span class="text-sm text-gray-700">{{ step.description || step }}</span>
                  </div>
                </div>
              </div>

              <!-- 预期结果 -->
              <div v-if="caseDetail.expected_result" class="section">
                <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                  <icon-vue icon="mdi:check-circle-outline" class="mr-2 text-green-600" />
                  预期结果
                </h5>
                <div class="border-l-4 border-green-400 rounded-lg bg-green-50 p-4">
                  <p class="text-sm text-gray-700">{{ caseDetail.expected_result }}</p>
                </div>
              </div>

              <!-- 前置条件 -->
              <div v-if="caseDetail.preconditions" class="section">
                <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                  <icon-vue icon="mdi:clipboard-list-outline" class="mr-2 text-gray-600" />
                  前置条件
                </h5>
                <div class="border-l-4 border-gray-400 rounded-lg bg-gray-50 p-4">
                  <p class="text-sm text-gray-700">{{ caseDetail.preconditions }}</p>
                </div>
              </div>

              <!-- 执行历史 -->
              <div v-if="caseDetail.execution_history && caseDetail.execution_history.length > 0" class="section">
                <h5 class="mb-3 flex items-center text-base font-semibold text-gray-800">
                  <icon-vue icon="mdi:history" class="mr-2 text-gray-600" />
                  最近执行记录
                </h5>
                <div class="space-y-2">
                  <div
                    v-for="(record, index) in caseDetail.execution_history.slice(0, 5)"
                    :key="index"
                    class="flex items-center justify-between rounded-lg bg-gray-50 p-3"
                  >
                    <div class="flex items-center space-x-3">
                      <span class="text-sm text-gray-600">{{ record.execution_date }}</span>
                      <span class="text-sm text-gray-600">执行人: {{ record.executor }}</span>
                    </div>
                    <span class="rounded px-2 py-1 text-xs" :class="getExecutionStatusClass(record.status)">
                      {{ getExecutionStatusText(record.status) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="py-8 text-center">
              <icon-vue icon="mdi:file-document-outline" class="mb-3 text-4xl text-gray-300" />
              <p class="mb-4 text-gray-500">暂无详细的测试用例信息</p>
              <p class="text-xs text-gray-400">该测试用例可能是从外部系统关联的，详细信息需要在原系统中查看</p>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 空状态 -->
    <div v-else class="py-8 text-center">
      <icon-vue icon="mdi:file-document-outline" class="mb-3 text-4xl text-gray-300" />
      <p class="text-gray-500">暂无测试用例详情</p>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import type { TestCaseCorrelation } from '@/types/precision-new/ai-analysis';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { computed, ref, watch } from 'vue';

// Props
interface Props {
  visible: boolean;
  testCase?: TestCaseCorrelation;
  adopted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  testCase: undefined,
  adopted: false,
});

// Emits
const emit = defineEmits(['update:visible', 'close', 'adopt']);

// 状态
const activeTab = ref('ai-analysis');
const adopting = ref(false);
const caseDetailLoading = ref(false);
const caseDetail = ref<any>(null);

// Computed
const modalTitle = computed(() => {
  if (!props.testCase) {
    return '测试用例详情';
  }
  return `${props.testCase.test_case_name} - 详细信息`;
});

// 监听用例变化，加载详情
watch(
  () => props.testCase,
  async (newTestCase) => {
    if (newTestCase && props.visible) {
      await loadCaseDetail(newTestCase.test_case_id);
    }
  },
  { immediate: true },
);

// 监听弹窗显示状态
watch(
  () => props.visible,
  async (visible) => {
    if (visible && props.testCase) {
      await loadCaseDetail(props.testCase.test_case_id);
    }
  },
);

// Methods
function handleClose() {
  emit('update:visible', false);
  emit('close');
}

function handleAdopt() {
  if (!props.testCase) {
    return;
  }

  adopting.value = true;
  emit('adopt', props.testCase.test_case_id);

  // 模拟采纳过程
  setTimeout(() => {
    adopting.value = false;
    message.success('测试用例采纳成功');
  }, 1000);
}

// Mock 获取测试用例详情
async function loadCaseDetail(caseId: string) {
  console.log('loadCaseDetail', caseId);
  caseDetailLoading.value = true;
  try {
    // 模拟API调用延迟
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock 数据
    caseDetail.value = {
      test_steps: [
        { description: '打开用户注册页面' },
        { description: '输入有效的用户名和密码' },
        { description: '点击注册按钮' },
        { description: '验证注册成功提示' },
      ],
      expected_result: '用户注册成功，页面显示成功提示信息，并跳转到登录页面',
      preconditions: '系统正常运行，数据库连接正常，用户名未被占用',
      execution_history: [
        {
          execution_date: '2024-01-15',
          executor: '张三',
          status: 'passed',
        },
        {
          execution_date: '2024-01-10',
          executor: '李四',
          status: 'failed',
        },
        {
          execution_date: '2024-01-05',
          executor: '王五',
          status: 'passed',
        },
      ],
    };
  } catch (error) {
    console.error('加载测试用例详情失败:', error);
    caseDetail.value = null;
  } finally {
    caseDetailLoading.value = false;
  }
}

function getPriorityText() {
  if (!props.testCase) {
    return '';
  }

  const priorityTexts = {
    critical: '关键',
    high: '高',
    medium: '中',
    low: '低',
  };
  return priorityTexts[props.testCase.test_case_priority as keyof typeof priorityTexts] || '普通';
}

function getPriorityClass() {
  if (!props.testCase) {
    return '';
  }

  const priorityClasses = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };
  return (
    priorityClasses[props.testCase.test_case_priority as keyof typeof priorityClasses] || 'bg-gray-100 text-gray-800'
  );
}

function getPriorityBorderClass() {
  if (!props.testCase) {
    return '';
  }

  const borderClasses = {
    critical: 'border-red-200',
    high: 'border-orange-200',
    medium: 'border-yellow-200',
    low: 'border-green-200',
  };
  return borderClasses[props.testCase.test_case_priority as keyof typeof borderClasses] || 'border-gray-200';
}

function getCorrelationScore() {
  if (!props.testCase) {
    return '0%';
  }
  return `${Math.round((props.testCase.correlation_score || 0) * 100)}%`;
}

function getRiskScore() {
  if (!props.testCase?.risk_score) {
    return '-';
  }
  return `${Math.round(props.testCase.risk_score * 100)}%`;
}

function getRiskBackgroundClass() {
  if (!props.testCase?.risk_score) {
    return 'bg-gray-50';
  }

  const score = props.testCase.risk_score;
  if (score >= 0.8) {
    return 'bg-red-50';
  }
  if (score >= 0.6) {
    return 'bg-orange-50';
  }
  if (score >= 0.4) {
    return 'bg-yellow-50';
  }
  return 'bg-green-50';
}

function getRiskTextClass() {
  if (!props.testCase?.risk_score) {
    return 'text-gray-600';
  }

  const score = props.testCase.risk_score;
  if (score >= 0.8) {
    return 'text-red-600';
  }
  if (score >= 0.6) {
    return 'text-orange-600';
  }
  if (score >= 0.4) {
    return 'text-yellow-600';
  }
  return 'text-green-600';
}

function getFileDisplayName(file: any) {
  if (typeof file === 'string') {
    return file.split('/').pop() || file;
  }
  if (file && file.file_path) {
    return file.file_path.split('/').pop() || file.file_path;
  }
  return file;
}

function getFileCorrelation(file: any) {
  if (file && typeof file === 'object' && file.correlation_score) {
    return (file.correlation_score * 100).toFixed(1);
  }
  return null;
}

function getDefaultRecommendationReason() {
  if (!props.testCase) {
    return '';
  }

  const score = Math.round((props.testCase.correlation_score || 0) * 100);
  const priority = props.testCase.test_case_priority;

  let reason = `该测试用例与代码变更的关联度为 ${score}%`;

  if (priority === 'critical' || priority === 'high') {
    reason += '，且优先级较高';
  }

  if (props.testCase.related_files && props.testCase.related_files.length > 0) {
    reason += `，涉及 ${props.testCase.related_files.length} 个相关文件`;
  }

  if (props.testCase.functions && props.testCase.functions.length > 0) {
    reason += `，覆盖 ${props.testCase.functions.length} 个函数`;
  }

  reason += '，建议纳入最小测试集以确保代码变更的质量。';

  return reason;
}

function getExecutionStatusText(status: string) {
  const statusTexts = {
    passed: '通过',
    failed: '失败',
    blocked: '阻塞',
    skipped: '跳过',
  };
  return statusTexts[status as keyof typeof statusTexts] || status;
}

function getExecutionStatusClass(status: string) {
  const statusClasses = {
    passed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    blocked: 'bg-yellow-100 text-yellow-800',
    skipped: 'bg-gray-100 text-gray-800',
  };
  return statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800';
}
</script>

<style scoped>
.test-case-detail-modal :deep(.ant-modal-content) {
  max-height: 90vh;
  overflow: hidden;
}

.test-case-detail-modal :deep(.ant-modal-body) {
  max-height: calc(90vh - 110px);
  overflow-y: auto;
  padding: 16px 24px;
}

.test-case-detail-content {
  font-size: 14px;
  line-height: 1.6;
}

.section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 1.5rem;
}

.section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

/* 滚动条样式 */
.test-case-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar {
  width: 8px;
}

.test-case-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.test-case-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.test-case-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
